<template>
  <control-wrapper
    v-bind="controlWrapper"
    :styles="styles"
    :isFocused="isFocused"
    :appliedOptions="appliedOptions"
  >
    <v-hover v-slot="{ hover }">
      <v-stripe-card
        :api-key="apiKey"
        :create="generate"
        :loading="loading"
        v-disabled-icon-focus
        :id="control.id + '-input'"
        :class="styles.control.input"
        :disabled="!control.enabled"
        :autofocus="appliedOptions.focus"
        :placeholder="appliedOptions.placeholder"
        :label="computedLabel"
        :hint="control.description"
        :persistent-hint="persistentHint()"
        :required="control.required"
        :error-messages="combinedErrors"
        :clearable="hover"
        v-bind="vuetifyProps('v-stripe-card')"
        @input="onStripeInput"
        @change="onStripeChange"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
      </v-stripe-card>
    </v-hover>
  </control-wrapper>
</template>

<script lang="ts">
import {
  and,
  ControlElement,
  isObjectControl,
  isStringControl,
  JsonFormsRendererRegistryEntry,
  JsonFormsSubStates,
  optionIs,
  or,
  rankWith,
} from '@jsonforms/core';
import {
  rendererProps,
  RendererProps,
  useJsonFormsControl,
} from '@jsonforms/vue2';
import {
  ControlWrapper,
  useTranslator,
  useVuetifyControl,
} from '@jsonforms/vue2-vuetify';
import isString from 'lodash/isString';
import { defineComponent, ref, inject } from 'vue';
import { VHover } from 'vuetify/lib';
import { VStripeCard } from '../components/VStripeCard';
import { DisabledIconFocus } from './directives';

const controlRenderer = defineComponent({
  name: 'stripe-card-control-renderer',
  components: {
    ControlWrapper,
    VHover,
    VStripeCard,
  },
  directives: {
    DisabledIconFocus,
  },
  props: {
    ...rendererProps<ControlElement>(),
  },
  setup(props: RendererProps<ControlElement>) {
    const t = useTranslator();
    const loading = ref(false);
    const stripeError = ref<string | undefined>(undefined);

    const jsonforms = inject<JsonFormsSubStates>('jsonforms');
    if (!jsonforms) {
      throw new Error(
        "'jsonforms' couldn't be injected. Are you within JsonForms?"
      );
    }

    return {
      ...useVuetifyControl(
        useJsonFormsControl(props),
        (value) => value || undefined
      ),
      t,
      loading,
      stripeError,
      jsonforms,
    };
  },
  computed: {
    apiKey(): string {
      let key = 
        this.jsonforms.config?.RUNBUGGY_FORMS_RENDERER_CONFIG?.stripe?.apiKey;
      if (isString(key)) {
        // prefer config
        return key;
      }

      key = (window as any)?.CONFIG?.stripe?.key;
      if (isString(key)) {
        // prefer spa-v2 key
        return key;
      }
      return (window as any)?.RUNBUGGY_FORMS_RENDERER_CONFIG?.stripe?.apiKey;
    },
    generate(): 'source' | 'token' {
      if (this.control.uischema.options?.format === 'stripe-token') {
        return 'token';
      }

      return 'source';
    },
    combinedErrors(): string | undefined {
      if (this.stripeError) {
        return this.stripeError;
      }

      return this.control.errors;
    },
  },
  methods: {
    onStripeInput(data: stripe.Token | stripe.Source) {
      if (data && data.id) {
        if (this.control.schema.type === 'string') {
          this.onChange(data.id);
        } else {
          this.onChange(data);
        }
      }
    },
    onStripeChange(response: stripe.elements.ElementChangeResponse) {
      if (response.error) {
        if (response.error.message) {
          this.stripeError = response.error.message;
        }
      } else {
        this.stripeError = undefined;
      }

      if (response.error || !response.complete || response.empty) {
        this.onChange(undefined);
      }
    },
  },
});

export default controlRenderer;

export const entry: JsonFormsRendererRegistryEntry = {
  renderer: controlRenderer,
  tester: rankWith(
    2,
    and(
      or(isStringControl, isObjectControl),
      or(
        optionIs('format', 'stripe-token'),
        optionIs('format', 'stripe-source')
      )
    )
  ),
};
</script>
